import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Globe, ChevronRight } from 'lucide-react';
import LanguageOption from './components/LanguageOption';
import LanguagePreview from './components/LanguagePreview';
import LanguageFeatures from './components/LanguageFeatures';
import { SUPPORTED_LANGUAGES, setLanguage } from '../../utils/i18n/config';

const LanguageSelection = () => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const handleLanguageSelect = (code) => {
    setSelectedLanguage(code);
  };

  const handleContinue = () => {
    // Save language preference using centralized function
    setLanguage(selectedLanguage);
    // Navigate to home
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Select Your Language</h1>
                <p className="text-xs text-gray-600 mt-0">Choose your preferred language for the best experience</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Language Selection */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-purple-50">
                <h2 className="text-base font-bold text-gray-900">Available Languages</h2>
                <p className="text-[11px] text-gray-600 mt-0">We support 10 languages to serve you better</p>
              </div>

              <div className="p-3">
                <div className="grid sm:grid-cols-2 gap-1.5 max-h-[calc(100vh-240px)] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {SUPPORTED_LANGUAGES?.map((language) => (
                    <LanguageOption
                      key={language?.code}
                      language={language}
                      isSelected={selectedLanguage === language?.code}
                      onClick={handleLanguageSelect}
                    />
                  ))}
                </div>

                {/* Continue Button */}
                <div className="mt-3 pt-2.5 border-t border-gray-100">
                  <button
                    onClick={handleContinue}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 text-sm sm:text-base"
                  >
                    <span>Continue with {SUPPORTED_LANGUAGES?.find(l => l?.code === selectedLanguage)?.name}</span>
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Preview & Features */}
          <div className="space-y-6">
            <LanguagePreview language={SUPPORTED_LANGUAGES?.find(l => l?.code === selectedLanguage)} />
            <LanguageFeatures />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelection;